import { t } from '../strings';
import type { MessageStatusName } from './messaging';

/**
 * 🔒 A Person's texts are one thread on the company number (piece 21,
 * *Communications seam*; decision 5210): the conversation's own `MessageLog`
 * rows on the Person's open mobile lines, both directions, oldest first, and
 * never a login code or another record's text to the same number. The phone's conversation and the website's Texts tile read the same
 * route and send through the same one, so both draw this answer.
 */
export const PEOPLE_PATH = '/people';

/** The thread's read and its send, under the Person they are with. */
export function personTextsPath(personId: string): string {
  return `${PEOPLE_PATH}/${encodeURIComponent(personId)}/texts`;
}

/** The longest text the seam carries, Twilio's own ceiling of ten segments. */
export const TEXT_BODY_MAX = 1600;

/** The newest rows the thread answers, drawn oldest first. */
export const TEXT_THREAD_ROWS = 200;

export type TextDirectionName = 'Outbound' | 'Inbound';

export interface TextThreadRow {
  id: string;
  direction: TextDirectionName;
  body: string;
  /** When the row was written, an ISO instant. */
  at: string;
  status: MessageStatusName;
  /** The Person's line the text went to or came from. */
  number: string;
}

export interface PersonTextsAnswer {
  personId: string;
  name: string;
  /** The Person's open mobile lines, in E.164, the first the one a send goes to. */
  lines: string[];
  /** Whether this viewer may send from here: a writer on the Person module, with a line to send to. */
  sends: boolean;
  rows: TextThreadRow[];
  /** The newest row's id, which a send names as the thread it answers. */
  newestId: string | null;
}

/** The send's body: the text, and the newest row the sender saw, or null on an empty thread. */
export interface SendTextInput {
  body: string;
  after?: string | null;
}

/**
 * 🔨 The send's `revision` (piece 21's idempotency rule, the Person as the
 * record): the newest row the sender saw joined to a digest of the text. A
 * retried send composes the same key and is one message; the same words
 * after a reply has landed compose a new one.
 */
export function textRevision(after: string | null | undefined, digest: string): string {
  return `${after && after.trim() !== '' ? after : 'none'}.${digest}`;
}

/** What `after` may hold: a row id, never free text inside the key. */
export function textAfterProblem(after: unknown): string | null {
  if (after === undefined || after === null) return null;
  if (typeof after !== 'string' || !/^[0-9a-fA-F-]{1,64}$/.test(after)) return 'The thread a text answers is named by a row of it.';
  return null;
}

const TEXT_STATUS_KEYS = {
  Queued: 'textThread.status.queued',
  Sent: 'textThread.status.sent',
  Delivered: 'textThread.status.delivered',
  Failed: 'textThread.status.failed',
  Undeliverable: 'textThread.status.undeliverable',
  Unknown: 'textThread.status.unknown',
} as const satisfies Record<MessageStatusName, string>;

/** The word an outbound row's status reads in, under its bubble. */
export function textStatusWord(status: MessageStatusName): string {
  return t(TEXT_STATUS_KEYS[status]);
}
